import vtkCellArray from '@kitware/vtk.js/Common/Core/CellArray';
import vtkPoints from '@kitware/vtk.js/Common/Core/Points';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkTubeFilter from '@kitware/vtk.js/Filters/General/TubeFilter';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';

import type { RGB, Vec3 } from '../../domain';

export class Path3DActor {
    private _actor: vtkActor | null = null;
    private _mapper: vtkMapper | null = null;
    private _tubeFilter: vtkTubeFilter | null = null;
    private _polyData: vtkPolyData | null = null;
    private _triangulatedPolyData: vtkPolyData | null = null;
    private _points: vtkPoints | null = null;
    private _lines: vtkCellArray | null = null;

    constructor(
        readonly points: Vec3[],
        readonly radius: number = 1,
        readonly color: RGB = [1, 1, 1],
        readonly opacity: number = 1
    ) {
        if (points.length < 2) {
            return;
        }

        const polydata = this.createPolydata(points);

        const tubeFilter = vtkTubeFilter.newInstance();
        tubeFilter.setCapping(true);
        tubeFilter.setNumberOfSides(8);
        tubeFilter.setRadius(radius);
        tubeFilter.setInputData(polydata);
        tubeFilter.setInputArrayToProcess(0, 'Scalars', 'PointData', 'Scalars');
        tubeFilter.update();

        // Convert triangle strips to triangles for CellPicker compatibility
        const triangulatedData = this.triangulateStrips(tubeFilter.getOutputData());

        const mapper = vtkMapper.newInstance();
        mapper.setInputData(triangulatedData);

        const actor = vtkActor.newInstance();
        actor.setMapper(mapper);

        actor.getProperty().setColor(...color);
        actor.getProperty().setOpacity(opacity);
        actor.getProperty().setSpecular(0);
        actor.getProperty().setSpecularPower(200);

        this._actor = actor;
        this._mapper = mapper;
        this._tubeFilter = tubeFilter;
        this._triangulatedPolyData = triangulatedData;
    }

    get actor() {
        return this._actor;
    }

    setRadius(radius: number): void {
        if (!this._tubeFilter || !this._mapper) return;

        this._tubeFilter.setRadius(radius);
        this._tubeFilter.update();

        const oldTriangulated = this._triangulatedPolyData;
        this._triangulatedPolyData = this.triangulateStrips(this._tubeFilter.getOutputData());
        this._mapper.setInputData(this._triangulatedPolyData);
        oldTriangulated?.delete();
    }

    dispose(): void {
        this._actor?.delete();
        this._mapper?.delete();
        this._tubeFilter?.delete();
        this._polyData?.delete();
        this._triangulatedPolyData?.delete();
        this._lines?.delete();
        this._points?.delete();

        this._points = null;
        this._lines = null;
        this._polyData = null;
        this._triangulatedPolyData = null;
        this._tubeFilter = null;
        this._mapper = null;
        this._actor = null;
    }

    /**
     * Convert triangle strips to explicit triangle polys.
     * vtkCellPicker in VTK.js does not reliably intersect triangle strips.
     */
    private triangulateStrips(tubeOutput: vtkPolyData): vtkPolyData {
        const strips = tubeOutput.getStrips();
        if (!strips || strips.getNumberOfCells() === 0) {
            return tubeOutput;
        }

        const stripData = strips.getData();
        const triData: number[] = [];

        let offset = 0;
        while (offset < stripData.length) {
            const nPts = stripData[offset];
            for (let i = 0; i < nPts - 2; i++) {
                const i0 = stripData[offset + 1 + i];
                const i1 = stripData[offset + 1 + i + 1];
                const i2 = stripData[offset + 1 + i + 2];
                // Alternate winding order to maintain consistent normals
                if (i % 2 === 0) {
                    triData.push(3, i0, i1, i2);
                } else {
                    triData.push(3, i1, i0, i2);
                }
            }
            offset += nPts + 1;
        }

        const result = vtkPolyData.newInstance();
        result.setPoints(tubeOutput.getPoints());
        result.setPolys(vtkCellArray.newInstance({ values: new Uint32Array(triData) }));

        return result;
    }

    private createPolydata(points: Vec3[]): vtkPolyData {
        const pointsData = vtkPoints.newInstance();
        pointsData.setData(Float64Array.from(points.flat()));

        const lines = vtkCellArray.newInstance();
        const nPoints = points.length;

        const lineData = new Uint32Array(nPoints + 1);
        lineData[0] = nPoints;
        for (let i = 0; i < nPoints; i++) {
            lineData[i + 1] = i;
        }
        lines.setData(lineData);

        const polyData = vtkPolyData.newInstance();
        polyData.setPoints(pointsData);
        polyData.setLines(lines);

        this._points = pointsData;
        this._lines = lines;
        this._polyData = polyData;

        return polyData;
    }
}
